import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { CLASS_LEVELS } from "@client/lib/class-levels"
import { getErrorMessage } from "@client/lib/errors"
import { uploadFile } from "@client/lib/uploads"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { Portal } from "./components/portal"
import { ActivityTimeline } from "./components/activity-timeline"
import { ConfirmModal } from "./components/confirm-modal"
import { SplitActionButton } from "./components/split-action-button"
import { DetailPageSkeleton } from "./components/skeleton"
import { AdminActivityEntityType } from "@shared/admin-activities"

type NominationStatus = "new" | "under_review" | "verified" | "rejected"
type NominationProgram = "scholarship" | "ability"

interface Nomination {
  id: string
  code: string
  childName: string
  dateOfBirth: string
  age: number
  currentClass: string | null
  state: string
  lga: string
  program: NominationProgram
  status: NominationStatus
  nominatorName: string
  nominatorTitle: string | null
  nominatorPhone: string | null
  nominatorEmail: string | null
  nominatorRelationship: string | null
  homeVisitCompleted: boolean
  schoolConfirmed: boolean
  guardianIdSeen: boolean
  photosReceived: boolean
  writtenCase: string | null
  attachments: { name: string; url: string }[] | null
  activity: { event: string; date: string }[] | null
  childId: string | null
  abilityBeneficiaryId: string | null
  submittedAt: string
}

const STATUS_COLORS: Record<NominationStatus, { bg: string; text: string }> = {
  new: { bg: "bg-gold-100", text: "text-gold-700" },
  under_review: { bg: "bg-blue-100", text: "text-blue-700" },
  verified: { bg: "bg-green-100", text: "text-green-700" },
  rejected: { bg: "bg-red-100", text: "text-red-700" },
}

const STATUS_LABELS: Record<NominationStatus, string> = {
  new: "New",
  under_review: "Under review",
  verified: "Verified",
  rejected: "Rejected",
}

const GENDERS = ["Male", "Female"] as const
const AGE_GROUPS = ["Child", "Teen", "University", "Adult"] as const
const REQUEST_ITEMS = ["Medical / health note", "Guardian ID", "Additional photos", "Birth certificate"] as const

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toISOString().split("T")[0] ?? dateString
}

function formatActivityDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "today"
  if (diffDays === 1) return "yesterday"
  if (diffDays < 7) return `${diffDays} days later`
  if (diffDays < 14) return "this week"
  return formatDate(dateString)
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22,4 12,14.01 9,11.01" />
    </svg>
  )
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  )
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
  )
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  )
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22,4 12,14.01 9,11.01" />
    </svg>
  )
}

export function NominationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("nominations")
  const canMutate = useHasPermission("nominations_mutate")
  const [nomination, setNomination] = useState<Nomination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false)
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [activityRefreshKey, setActivityRefreshKey] = useState(0)
  const modalRef = useRef<HTMLDivElement>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const [approveForm, setApproveForm] = useState({
    gender: "Male",
    classLevel: "Primary 1",
    ageGroup: "Child",
    school: "",
    notes: "",
  })
  const [respondByDate, setRespondByDate] = useState("")
  const [requestItems, setRequestItems] = useState<string[]>([...REQUEST_ITEMS])
  const [requestNote, setRequestNote] = useState("")

  const fetchNomination = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.nominations[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setNomination(body.data as Nomination)
      }
    } catch {
      setError("Failed to load nomination")
    } finally {
      setLoading(false)
    }
  }

  const reloadPageData = async () => {
    await fetchNomination()
    setActivityRefreshKey((key) => key + 1)
  }

  useEffect(() => {
    if (canView) {
      void fetchNomination()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const handleChecklistChange = async (field: string, value: boolean) => {
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.nominations[":id"].$patch({
        param: { id },
        json: { [field]: value },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setNomination(body.data as Nomination)
        toast.success("Checklist updated")
        await reloadPageData()
      }
    } catch {
      toast.error("Failed to update checklist")
    } finally {
      setSaving(false)
    }
  }

  const handleReject = async () => {
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.nominations[":id"].reject.$post({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Nomination rejected")
      navigate("/admin/nominations")
    } catch {
      toast.error("Failed to reject nomination")
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !canMutate) return
    const currentNomination = nomination
    if (!currentNomination) return
    setShowApproveConfirmation(true)
  }

  const confirmApprove = async () => {
    if (!id || !canMutate) return
    const currentNomination = nomination
    if (!currentNomination) return
    setSaving(true)
    try {
      const approvalDetails = currentNomination.program === "ability"
        ? {
            gender: approveForm.gender.toLowerCase() as "male" | "female",
            ageGroup: approveForm.ageGroup.toLowerCase() as "child" | "teen" | "university" | "adult",
            notes: approveForm.notes.trim() || undefined,
          }
        : {
            gender: approveForm.gender.toLowerCase() as "male" | "female",
            classLevel: approveForm.classLevel,
            school: approveForm.school.trim() || undefined,
            notes: approveForm.notes.trim() || undefined,
          }
      const res = await client.api.admin.nominations[":id"].approve.$post({
        param: { id },
        json: approvalDetails,
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if (currentNomination.program === "ability") {
        const beneficiaryId = "data" in body ? body.data.abilityBeneficiary?.id : null
        toast.success("Nomination approved and added to Ability Program")
        navigate(beneficiaryId ? `/admin/ability/${beneficiaryId}` : "/admin/ability")
      } else {
        toast.success("Nomination approved and child added to directory")
        navigate("/admin/children")
      }
      setShowApproveConfirmation(false)
      setShowApproveModal(false)
    } catch {
      toast.error("Failed to approve nomination")
    } finally {
      setSaving(false)
    }
  }

  const handleAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || !id || !canMutate) return

    setUploadingAttachment(true)
    try {
      const uploaded = await uploadFile(file, "nomination-attachment")
      const res = await client.api.admin.nominations[":id"].attachments.$post({
        param: { id },
        json: uploaded,
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) setNomination(body.data as Nomination)
      toast.success("Attachment uploaded")
      await reloadPageData()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload attachment")
    } finally {
      setUploadingAttachment(false)
    }
  }

  const openRequestModal = () => {
    setRespondByDate("")
    setRequestItems([...REQUEST_ITEMS])
    setRequestNote("")
    setShowRequestModal(true)
  }

  const handleRequestInfo = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!id || !canMutate || !nomination) return
    if (!nomination.nominatorEmail) {
      toast.error("This nominator does not have an email address")
      return
    }
    if (requestItems.length === 0) {
      toast.error("Select at least one requested item")
      return
    }

    setSaving(true)
    try {
      const res = await client.api.admin.nominations[":id"]["request-info"].$post({
        param: { id },
        json: {
          respondBy: respondByDate || undefined,
          items: requestItems,
          note: requestNote.trim() || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) setNomination(body.data as Nomination)
      toast.success("Request sent by email")
      setShowRequestModal(false)
      await reloadPageData()
    } catch {
      toast.error("Failed to send request")
    } finally {
      setSaving(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowApproveModal(false)
      setShowRequestModal(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Nomination</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view this nomination.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !nomination) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/nominations")}
          className="inline-flex items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "Nomination not found"}</p>
        </div>
      </div>
    )
  }

  const allChecklistComplete =
    nomination.homeVisitCompleted &&
    nomination.schoolConfirmed &&
    nomination.guardianIdSeen &&
    nomination.photosReceived

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/nominations")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-ink/50">Nomination · {nomination.code} · {nomination.program === "ability" ? "Ability Program" : "Scholarship"}</p>
            <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">{nomination.childName}</h1>
            <p className="mt-1 text-sm text-ink/60">
              Age {nomination.age} · {nomination.state}, {nomination.lga} · Submitted {formatDate(nomination.submittedAt)}
            </p>
            <span
              className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                STATUS_COLORS[nomination.status].bg
              } ${STATUS_COLORS[nomination.status].text}`}
            >
              {STATUS_LABELS[nomination.status]}
            </span>
          </div>

          {canMutate && nomination.status !== "verified" && nomination.status !== "rejected" && (
              <div className="flex flex-wrap gap-2">
               <SplitActionButton
                 label="Request more info"
                 icon={<MessageIcon className="h-4 w-4" />}
                 onClick={openRequestModal}
                 disabled={saving}
                 primaryDisabled={!nomination.nominatorEmail}
                 items={[
                   {
                     label: nomination.program === "ability" ? "Approve & add to Ability Program" : "Approve & add to children",
                     icon: <CheckCircleIcon className="h-4 w-4" />,
                     onClick: () => setShowApproveModal(true),
                   },
                   { label: "Reject", icon: <XIcon className="h-4 w-4" />, onClick: () => setShowRejectConfirmation(true) },
                 ]}
               />
             </div>
          )}
        </div>
      </div>

      {/* Nominee Snapshot */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">{nomination.program === "ability" ? "Nominee Snapshot" : "Child Snapshot"}</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs text-ink/50">Name</p>
            <p className="text-sm font-medium text-ink">{nomination.childName}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Age</p>
            <p className="text-sm font-medium text-ink">{nomination.age}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Current class</p>
            <p className="text-sm font-medium text-ink">{nomination.currentClass || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">State</p>
            <p className="text-sm font-medium text-ink">{nomination.state}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">LGA</p>
            <p className="text-sm font-medium text-ink">{nomination.lga}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Submitted</p>
            <p className="text-sm font-medium text-ink">{formatDate(nomination.submittedAt)}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Reference</p>
            <p className="text-sm font-medium text-ink">{nomination.code}</p>
          </div>
        </div>
      </div>

      {/* Nominator */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Nominator</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs text-ink/50">{nomination.nominatorName}</p>
            <p className="text-sm font-medium text-ink">{nomination.nominatorTitle || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Phone</p>
            <p className="text-sm font-medium text-ink">{nomination.nominatorPhone || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Email</p>
            <p className="text-sm font-medium text-ink">{nomination.nominatorEmail || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Relationship to child</p>
            <p className="text-sm font-medium text-ink">{nomination.nominatorRelationship || "—"}</p>
          </div>
        </div>
      </div>

      {/* Verification Checklist */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Verification Checklist</h2>
        <div className="mt-4 space-y-3">
          {[
            { key: "homeVisitCompleted", label: "Home visit completed", value: nomination.homeVisitCompleted },
            { key: "schoolConfirmed", label: "School confirmed", value: nomination.schoolConfirmed },
            { key: "guardianIdSeen", label: "Guardian ID seen", value: nomination.guardianIdSeen },
            { key: "photosReceived", label: "Photos & documents received", value: nomination.photosReceived },
          ].map((item) => (
            <label
              key={item.key}
              className={`flex items-center gap-3 ${canMutate && nomination.status !== "verified" && nomination.status !== "rejected" ? "cursor-pointer" : ""}`}
            >
              {canMutate && nomination.status !== "verified" && nomination.status !== "rejected" ? (
                <input
                  type="checkbox"
                  checked={item.value}
                  onChange={(e) => handleChecklistChange(item.key, e.target.checked)}
                  disabled={saving}
                  className="h-5 w-5 rounded border-gold-500 text-forest-700 focus:ring-forest-700"
                />
              ) : item.value ? (
                <CheckIcon className="h-5 w-5 text-green-600" />
              ) : (
                <InfoIcon className="h-5 w-5 text-ink/40" />
              )}
              <span className="text-sm text-ink">{item.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 rounded-lg bg-cream-100 p-3">
          <p className="text-sm text-ink/70">
            Complete all items before approving to move this nomination into the {nomination.program === "ability" ? "Ability Program" : "Children directory"}.
          </p>
        </div>
      </div>

      {/* Written Case */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Written Case</h2>
        <div className="mt-4 rounded-lg bg-cream-100 p-3">
          <p className="text-sm text-ink">{nomination.writtenCase || "No written case provided."}</p>
        </div>
      </div>

      {/* Attachments */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Attachments</h2>
          {canMutate && (
            <>
              <input
                ref={attachmentInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={handleAttachmentChange}
              />
              <button
                type="button"
                disabled={uploadingAttachment}
                onClick={() => attachmentInputRef.current?.click()}
                className="rounded-md border border-ink/20 px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-cream-100 disabled:opacity-50"
              >
                {uploadingAttachment ? "Uploading..." : "Upload attachment"}
              </button>
            </>
          )}
        </div>
        {nomination.attachments && nomination.attachments.length > 0 ? (
          <div className="mt-4 space-y-2">
            {nomination.attachments.map((attachment, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg border border-gold-500/50 bg-cream-50 p-3"
              >
                <div className="flex items-center gap-3">
                  <FileIcon className="h-5 w-5 text-ink/40" />
                  <span className="text-sm text-ink">{attachment.name}</span>
                </div>
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md border border-ink/20 px-3 py-1 text-xs font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  View
                </a>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-ink/50">No attachments.</p>
        )}
      </div>

      <ActivityTimeline
        entityType={AdminActivityEntityType.Nomination}
        entityId={nomination.id}
        title="Activity"
        refreshKey={activityRefreshKey}
        fallback={
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Activity</h2>
            {nomination.activity && nomination.activity.length > 0 ? (
              <div className="mt-4 space-y-3">
                {nomination.activity.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-forest-700" />
                    <div>
                      <span className="text-sm font-medium text-ink">{item.event}</span>
                      <span className="text-sm text-ink/50"> · {formatActivityDate(item.date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-ink/50">No activity recorded.</p>
            )}
          </div>
        }
      />

      {/* Approve Modal */}
      {showApproveModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">{nomination.program === "ability" ? "Approve & Add to Ability Program" : "Approve & Add to Children"}</h2>
            <p className="mt-1 text-sm text-ink/60">
              {nomination.code} · {nomination.childName}
            </p>
            <form onSubmit={handleApprove} className="mt-6 space-y-4">
              {nomination.program === "ability" ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Gender *"
                    value={approveForm.gender}
                    onChange={(value) => setApproveForm({ ...approveForm, gender: value })}
                    options={GENDERS}
                  />
                  <Select
                    label="Age group *"
                    value={approveForm.ageGroup}
                    onChange={(value) => setApproveForm({ ...approveForm, ageGroup: value })}
                    options={AGE_GROUPS}
                  />
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Select
                      label="Gender *"
                      value={approveForm.gender}
                      onChange={(value) => setApproveForm({ ...approveForm, gender: value })}
                      options={GENDERS}
                    />
                    <Select
                      label="Class Level *"
                      value={approveForm.classLevel}
                      onChange={(value) => setApproveForm({ ...approveForm, classLevel: value })}
                      options={CLASS_LEVELS}
                    />
                  </div>

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">School <span className="text-xs font-normal text-ink/50">(optional)</span></span>
                    <input
                      type="text"
                      value={approveForm.school}
                      onChange={(e) => setApproveForm({ ...approveForm, school: e.target.value })}
                      className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                      placeholder="School name"
                    />
                  </label>
                </>
              )}

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Notes</span>
                <textarea
                  value={approveForm.notes}
                  onChange={(e) => setApproveForm({ ...approveForm, notes: e.target.value })}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Additional notes..."
                />
              </label>

              {!allChecklistComplete && (
                <div className="rounded-lg border border-gold-500 bg-gold-50 p-3">
                  <p className="text-sm text-gold-700">
                    Not all verification checklist items are complete. You can still approve, but this is not recommended.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowApproveModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Approving..." : "Approve & Add"}
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      <ConfirmModal
        open={showApproveConfirmation}
        title="Approve nomination?"
        message={`This will add ${nomination.childName} to ${nomination.program === "ability" ? "the Ability Program" : "the children directory"}.`}
        confirmLabel="Approve & add"
        confirmVariant="primary"
        onConfirm={() => void confirmApprove()}
        onCancel={() => setShowApproveConfirmation(false)}
      />
      <ConfirmModal
        open={showRejectConfirmation}
        title="Reject nomination?"
        message={`Are you sure you want to reject ${nomination.childName}'s nomination?`}
        confirmLabel="Reject nomination"
        onConfirm={() => {
          setShowRejectConfirmation(false)
          void handleReject()
        }}
        onCancel={() => setShowRejectConfirmation(false)}
      />

      {/* Request More Info Modal */}
      {showRequestModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Request more info</h2>
            <p className="mt-1 text-sm text-ink/60">
              {nomination.code} · {nomination.childName}
            </p>
            <form className="mt-6 space-y-4" onSubmit={handleRequestInfo}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="mb-1.5 block text-sm font-medium text-ink">Send via</span>
                  <div className="flex h-[45px] items-center rounded-lg border border-gold-500 bg-cream-50 px-4 text-sm text-ink">
                    Email
                  </div>
                </div>
                <DatePicker
                  label="Respond by"
                  value={respondByDate}
                  onChange={setRespondByDate}
                  min={new Date()}
                  popoverAlign="right"
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-ink">Request items</p>
                <div className="space-y-2">
                  {REQUEST_ITEMS.map((item) => (
                    <label key={item} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requestItems.includes(item)}
                        onChange={(event) => {
                          setRequestItems((current) => event.target.checked
                            ? [...current, item]
                            : current.filter((selected) => selected !== item))
                        }}
                        className="h-5 w-5 rounded border-gold-500 text-forest-700 focus:ring-forest-700"
                      />
                      <span className="text-sm text-ink">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Additional note <span className="font-normal text-ink/50">(optional)</span></span>
                  <textarea
                    rows={4}
                    className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-100 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                    placeholder="Optional note, for example: Please include clear photos taken this week."
                  value={requestNote}
                  onChange={(event) => setRequestNote(event.target.value)}
                />
              </label>

              <div className="flex justify-start gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Sending..." : "Send request"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
